import { visit } from 'unist-util-visit';

import {
  CITATION_LINK_TEXT,
  CITATION_URL_PATTERN,
} from '../../../utils/sources.utils';
import {
  INLINE_CITATION_HAST_PROP,
  INLINE_CITATION_WEB,
} from '../MarkdownRenderer.constants';

import type { StreamingCitationSources } from '../../../types';
import type { Element, Root } from 'hast';

type RehypeCitationLinksOptions = {
  sources?: StreamingCitationSources;
};

const HTTP_URL_PATTERN = /^https?:\/\//i;

/**
 * A `[source](https://...)` link to a public website. The link text must be
 * exactly the citation text so that ordinary markdown links in the same
 * content keep rendering as links.
 */
function isWebCitationLink(node: Element, href: string): boolean {
  if (!HTTP_URL_PATTERN.test(href)) return false;
  if (node.children.length !== 1) return false;

  const [child] = node.children;
  return child.type === 'text' && child.value === CITATION_LINK_TEXT;
}

/**
 * Rehype plugin that marks citation <a> tags with a data-inline-citation attribute.
 *
 * Detection:
 * 1. href matches the CITATION_URL_PATTERN (static pattern)
 * 2. href matches any source URL from the provided sources array
 * 3. href is a public http(s) URL and the link text is the citation text
 *    ("source"); the attribute is then set to INLINE_CITATION_WEB
 *
 * The CustomLink component then reads this attribute to decide whether to render
 * an InlineCitation or a plain anchor.
 */
export const rehypeCitationLinks = (
  options?: RehypeCitationLinksOptions,
): ((tree: Root) => void) => {
  const sources = options?.sources;

  return (tree: Root) => {
    visit(tree, 'element', (node: Element) => {
      if (node.tagName !== 'a') return;

      const href = node.properties?.href;
      if (typeof href !== 'string') return;

      const matchesIndexerPattern = CITATION_URL_PATTERN.test(href);
      const matchesSourceUrl =
        sources?.some((source) => source?.url === href) ?? false;

      if (matchesIndexerPattern || matchesSourceUrl) {
        node.properties = {
          ...node.properties,
          [INLINE_CITATION_HAST_PROP]: 'true',
        };
        return;
      }

      if (isWebCitationLink(node, href)) {
        node.properties = {
          ...node.properties,
          [INLINE_CITATION_HAST_PROP]: INLINE_CITATION_WEB,
        };
      }
    });
  };
};
